import { Observable, filter, map, merge, switchMap, tap } from 'rxjs';

import {
  DeleteWa_CustomerCrmNoteMutationVariables,
  SaveWa_CustomerCrmNoteMutationVariables,
} from '../../../../../graphql/generatedModel';
import { responseHandler } from '../../../../../shared/responseHandler/responseHandler';
import { Pub, State, Sub } from '../../../../../shared/state/state.abstract';
import { defaultCustomerCRMNotes } from '../../../loaders/crmTab.resolver';
import {
  CustomerCRMNote,
  CustomerCRMNotesRes,
  DeleteCustomerCRMNoteRes,
  SaveCustomerCRMNoteRes,
  customerService,
} from '../../../services/customer.service';
import { customersListState } from '../../../states/customersList.state';
import { ITabState, tabStateModel } from '../../../states/tabState.model';
import { customerTabLoadingService } from '../customerTabLoading.service';

class PubImpl extends Pub<ICRMTabState> {
  initCRMNotesData(customerCRMNotes: ITabState['customerCRMNotes'], customerId: string) {
    this.emit('initCRMNotesData', { customerCRMNotes, customerId });
  }

  /* One action for both write modes, as in the Windows note dialog: a payload without
  an id creates a note, one with an id rewrites the text of that note. The customer is
  not part of the payload - the state already tracks which one the tab is showing. */
  saveRecord(recordToSave: ICRMTabState['recordToSave']) {
    this.emit('saveRecord', { recordToSave });
  }

  deleteRecord(recordToRemove: DeleteWa_CustomerCrmNoteMutationVariables) {
    this.emit('deleteRecord', { recordToRemove });
  }

  refresh() {
    this.emit('refresh', {});
  }
}

class SubImpl extends Sub<ICRMTabState> {
  protected actionHandlers(): Observable<ICRMTabState> {
    return merge(
      this.selectedCustomer(),
      this.refresh(),
      this.saveRecord(),
      this.deleteRecord(),
    ).pipe(
      tap((state) => {
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
    );
  }

  private saveRecord(): Observable<ICRMTabState> {
    return this.actionListener('saveRecord').pipe(
      filter(({ recordToSave }) => !!recordToSave),
      tap(({ recordToSave, customerId }) => {
        customerService.pub.saveCustomerCRMNote({
          dataToSave: { ...recordToSave!, customerId },
        });
        customerTabLoadingService.pub.loading(true);
      }),
      switchMap((state) =>
        customerService.sub.saveCustomerCRMNote().pipe(
          responseHandler<SaveCustomerCRMNoteRes | undefined>({
            success: () => 'customer.crm_note_saved',
            customErrorHandler: () => 'common.error_chnages_not_saved',
          }),
          map((savedNote) => {
            const newState = structuredClone(state);
            customerTabLoadingService.pub.loading(false);

            if (savedNote) {
              const index = newState.customerCRMNotes.findIndex(
                (note) => String(note.id) === String(savedNote.id),
              );

              if (index === -1) {
                newState.customerCRMNotes.push(savedNote);
              } else {
                newState.customerCRMNotes[index] = savedNote;
              }
              /* The server orders by addedOn; a new note is the newest, but an edit can
              land on any row, so re-sort rather than assume the push kept the order. */
              newState.customerCRMNotes = sortNotesByAddedOn(newState.customerCRMNotes);
              newState.savedNoteId = savedNote.id;
            }

            newState.recordToSave = null;
            return newState;
          }),
        ),
      ),
    );
  }

  private deleteRecord(): Observable<ICRMTabState> {
    return this.actionListener('deleteRecord').pipe(
      filter(({ recordToRemove }) => !!recordToRemove),
      tap(({ recordToRemove }) => {
        customerService.pub.deleteCustomerCRMNote({ id: recordToRemove!.id });
        customerTabLoadingService.pub.loading(true);
      }),
      switchMap((state) =>
        customerService.sub.deleteCustomerCRMNote().pipe(
          responseHandler<DeleteCustomerCRMNoteRes | undefined>({
            success: () => 'customer.crm_note_deleted',
            customErrorHandler: () => 'common.error_chnages_not_saved',
          }),
          map((isDeleted) => {
            const newState = structuredClone(state);
            customerTabLoadingService.pub.loading(false);

            if (isDeleted) {
              newState.customerCRMNotes = newState.customerCRMNotes.filter(
                (note) => String(note.id) !== String(state.recordToRemove?.id),
              );
              newState.savedNoteId = null;
            }

            newState.recordToRemove = null;
            return newState;
          }),
        ),
      ),
    );
  }

  private refresh(): Observable<ICRMTabState> {
    return this.actionListener('refresh').pipe(
      switchMap((state) => {
        customerTabLoadingService.pub.loading(true);
        return this.notes(state, state.customerId);
      }),
    );
  }

  private selectedCustomer(): Observable<ICRMTabState> {
    return customersListState.sub.state().pipe(
      filter(({ action }) => action === 'selectRecord'),
      filter(({ selectedRecord }) => typeof selectedRecord?.id === 'string'),
      switchMap(({ selectedRecord }) => {
        customerTabLoadingService.pub.loading(true);
        const state = this.stream$.getValue();
        state.action = 'list.selectedRecord';
        return this.notes(state, selectedRecord!.id);
      }),
    );
  }

  private notes(state: ICRMTabState, customerId: string): Observable<ICRMTabState> {
    const notes = customerService.sub.getCustomerCRMNotes().pipe(
      responseHandler<CustomerCRMNotesRes>({
        errorReturnType: defaultCustomerCRMNotes,
      }),
      map((data) => {
        state.customerCRMNotes = data;
        state.customerId = customerId;
        state.savedNoteId = null;
        customerTabLoadingService.pub.loading(false);
        return state;
      }),
    );
    customerService.pub.getCustomerCRMNotes({ customerId });
    return notes;
  }
}

/* Windows lists the notes oldest first (Customer_RecCRM sets DefOrderClause to AddedOn),
so the newest note is the bottom row. A note with no usable addedOn sorts last: it can only
come from a row Windows left unstamped, and the grid has no better place for it. */
export function sortNotesByAddedOn(notes: CustomerCRMNotesRes): CustomerCRMNotesRes {
  /* An unparseable stamp has to become a real number, not NaN: every comparison against
  NaN is false, so a NaN in the comparator scrambles the whole order, not just that row. */
  const stampOf = (addedOn: CustomerCRMNote['addedOn']) => {
    const parsed = addedOn ? Date.parse(addedOn) : Number.NaN;
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  };

  return [...notes].sort((a, b) => {
    const left = stampOf(a.addedOn);
    const right = stampOf(b.addedOn);

    if (left === right) {
      return Number(a.id) - Number(b.id);
    }
    return left - right;
  });
}

class CRMTabState extends State<ICRMTabState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const crmTabState = new CRMTabState({
  ...tabStateModel,
  action: undefined,
  customerCRMNotes: [],
  recordToSave: null,
  recordToRemove: null,
  savedNoteId: null,
});

export interface ICRMTabState extends Pick<ITabState, 'customerId' | 'customerCRMNotes'> {
  action:
    | undefined
    | 'initCRMNotesData'
    | 'list.selectedRecord'
    | 'refresh'
    | 'saveRecord'
    | 'deleteRecord'
    | 'internalUpdate';
  recordToSave: Omit<SaveWa_CustomerCrmNoteMutationVariables['dataToSave'], 'customerId'> | null;
  recordToRemove: DeleteWa_CustomerCrmNoteMutationVariables | null;
  // Id of the note the last save wrote, so the table can keep it selected.
  savedNoteId: CustomerCRMNote['id'] | null;
}
